import { useEffect, useMemo, useState } from "react";
import { queryClient } from "../../../Utils/query/queryClient";
import type { Tweaks } from "./types";
import {
  loaderTweaksFromCache,
  toWebsiteLoaderTweaksCache,
  type WebsiteLoaderTweaksCache,
} from "./templateSettingsMap";
import { websiteTemplateBootstrapQueryKey } from "./websiteTemplateBootstrap";

const STORAGE_PREFIX = "website_tpl_loader_tweaks_v1:";

/** Tweaks that arrived from the network during this page load, before the heavy bootstrap finishes. */
const liveLoaderTweaks = new Map<string, Tweaks>();
const liveLoaderListeners = new Set<() => void>();

function storageKey(username: string): string {
  return `${STORAGE_PREFIX}${username.trim().toLowerCase()}`;
}

export function readCachedWebsiteLoaderTweaks(
  username: string,
): Tweaks | null {
  const key = username.trim().toLowerCase();
  if (!key || typeof localStorage === "undefined") return null;
  try {
    const raw = localStorage.getItem(storageKey(key));
    if (!raw) return null;
    const parsed = JSON.parse(raw) as WebsiteLoaderTweaksCache;
    if (!parsed || typeof parsed !== "object") return null;
    return loaderTweaksFromCache(parsed);
  } catch {
    return null;
  }
}

export function writeCachedWebsiteLoaderTweaks(
  username: string,
  tweaks: Tweaks,
): void {
  const key = username.trim().toLowerCase();
  if (!key || typeof localStorage === "undefined") return;
  try {
    const payload = toWebsiteLoaderTweaksCache(tweaks);
    localStorage.setItem(storageKey(key), JSON.stringify(payload));
  } catch {
    // ignore quota / private mode
  }
}

export function clearCachedWebsiteLoaderTweaks(username: string): void {
  const key = username.trim().toLowerCase();
  if (!key || typeof localStorage === "undefined") return;
  try {
    localStorage.removeItem(storageKey(key));
  } catch {
    /* ignore */
  }
}

/** Hand the database palette to the loader without waiting for the rest of bootstrap. */
export function publishLiveWebsiteLoaderTweaks(
  username: string,
  tweaks: Tweaks,
): void {
  const key = username.trim().toLowerCase();
  if (!key) return;
  liveLoaderTweaks.set(key, tweaks);
  // Persist so the next hard reload (dashboard Suspense / boot cover) paints
  // the real website palette instead of Default white.
  writeCachedWebsiteLoaderTweaks(key, tweaks);
  liveLoaderListeners.forEach((fn) => fn());
}

export function readLiveWebsiteLoaderTweaks(username: string): Tweaks | null {
  const key = username.trim().toLowerCase();
  if (!key) return null;
  return liveLoaderTweaks.get(key) ?? null;
}

export function subscribeLiveWebsiteLoaderTweaks(listener: () => void): () => void {
  liveLoaderListeners.add(listener);
  return () => {
    liveLoaderListeners.delete(listener);
  };
}

/** Database palette for this visit, once bootstrap publishes live loader tweaks. */
export function useLiveWebsiteLoaderTweaks(
  username?: string | null,
): Tweaks | null {
  const [tick, setTick] = useState(0);
  useEffect(
    () => subscribeLiveWebsiteLoaderTweaks(() => setTick((n) => n + 1)),
    [],
  );
  return useMemo(
    () => (username ? readLiveWebsiteLoaderTweaks(username) : null),
    [username, tick],
  );
}

/**
 * First-paint loader theme for dashboard / public `/{username}`.
 * localStorage only — never fall back to react-query (per-tab RQ often still
 * holds the previous palette after a theme Publish).
 *
 * Cache miss → callers show a white pending loader until bootstrap returns,
 * then write the fetched theme here for the next visit.
 */
export function resolveWebsiteLoaderTweaks(username: string): Tweaks | null {
  const key = username.trim().toLowerCase();
  if (!key) return null;
  return readCachedWebsiteLoaderTweaks(key);
}

/**
 * After Publish: drop stale react-query bootstrap, then store the published
 * palette so the next dashboard / public reload paints the correct theme
 * immediately (no Default / white @handle flash while settings refetch).
 */
export function syncPublishedWebsiteLoaderTheme(
  username: string,
  tweaks?: Tweaks,
): void {
  const key = username.trim().toLowerCase();
  if (!key) return;
  try {
    queryClient.removeQueries(websiteTemplateBootstrapQueryKey(key));
  } catch {
    /* ignore */
  }
  if (tweaks) {
    writeCachedWebsiteLoaderTweaks(key, tweaks);
    publishLiveWebsiteLoaderTweaks(key, tweaks);
  } else {
    clearCachedWebsiteLoaderTweaks(key);
  }
}
